import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import type { JSONContent } from '@tiptap/react'
import { motion } from 'framer-motion'
import { Check, Cloud, Send, X } from '@/components/icons'
import {
  ARTICLE_TAG_MAX,
  ARTICLE_TAGS_MAX,
  ARTICLE_TITLE_MAX,
  ARTICLE_TITLE_MIN,
  createArticleSchema,
  type ArticleStatus,
  type CreateArticleInput,
} from '@zoxo/shared'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useLeaveGuard } from '@/lib/use-leave-guard'
import {
  useCreateArticle,
  useSubmitArticle,
  useUpdateArticle,
} from '../use-article-mutations'
import { describeSubmitError } from '../../../lib/describe-submit-error'
import { RichEditor } from './rich-editor'

export interface ArticleEditorInitial {
  title: string
  body: JSONContent | undefined
  tags: string[]
  /** Lifecycle status (edit mode). undefined in create mode → treated as draft. */
  status?: ArticleStatus
}

const EMPTY: ArticleEditorInitial = { title: '', body: undefined, tags: [] }

/** Plain-text length of a tiptap doc (for the live 글자 수). */
function countText(doc?: JSONContent): number {
  if (!doc) return 0
  let n = 0
  const walk = (node: JSONContent) => {
    if (typeof node.text === 'string') n += node.text.length
    node.content?.forEach(walk)
  }
  walk(doc)
  return n
}

function isNonEmptyDoc(doc: JSONContent | undefined): boolean {
  if (!doc) return false
  const has = (node: JSONContent): boolean => {
    if (node.type === 'image') return true
    if (typeof node.text === 'string' && node.text.trim() !== '') return true
    return (node.content ?? []).some(has)
  }
  return has(doc)
}

/**
 * Self-contained magazine-style article editor — UI matched 1:1 to Seasonly:
 * top bar (초안 저장 / 수정 게시) + status row (badge · 글자 수 · autosave) + title
 * + rich editor + 작성 팁.
 *
 * zoxo has no separate draft state (only isActive = 검수 대기 / 발행됨), so the
 * Seasonly DRAFT/PUBLISH state machine is mapped onto POST(create=pending) /
 * PATCH(update): autosave + "초안 저장" PATCH in edit mode; "수정 게시" saves then
 * opens the detail. (No poll button — polls are a separate pillar.)
 */
export function ArticleEditor({
  articleId,
  initial = EMPTY,
}: {
  articleId?: string
  initial?: ArticleEditorInitial
}) {
  const navigate = useNavigate()
  const isEdit = articleId !== undefined

  const create = useCreateArticle()
  const update = useUpdateArticle(articleId ?? '')
  const submit = useSubmitArticle(articleId ?? '')

  const status: ArticleStatus = isEdit ? (initial.status ?? 'pending') : 'draft'

  const [title, setTitle] = useState(initial.title)
  const [body, setBody] = useState<JSONContent | undefined>(initial.body)
  const [tags, setTags] = useState<string[]>(initial.tags)
  const [tagDraft, setTagDraft] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [error, setError] = useState<string | null>(null)

  /*
    **나가기 확인.** 마지막으로 저장된 상태와 다르면 묻는다. 수정 화면은 자동 저장이
    돌아 저장 직후엔 안 묻는다. 저장 요청을 보낼 때의 상태를 찍어 두고 성공하면 그게 기준이 된다.
  */
  const snapshotOf = (t: string, b: JSONContent | undefined, tg: string[], draft: string) =>
    JSON.stringify([t, b ?? null, tg, draft.trim()])
  const [savedSnap, setSavedSnap] = useState(() => snapshotOf(initial.title, initial.body, initial.tags, ''))
  const currentSnap = () => snapshotOf(title, body, tags, tagDraft)
  const allowLeave = useLeaveGuard(currentSnap() !== savedSnap)

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
  }, [])

  const charCount = countText(body)
  const hasTitle = title.trim().length > 0
  const canAddTag = tags.length < ARTICLE_TAGS_MAX

  // ── Autosave (edit mode only — create would prematurely submit for review). ──
  const buildPayload = useCallback(
    (tagsArg: string[]) => ({
      title: title.trim(),
      body: body as Record<string, unknown>,
      ...(tagsArg.length > 0 ? { tags: tagsArg } : {}),
    }),
    [title, body],
  )

  const scheduleAutosave = useCallback(() => {
    if (!isEdit) return
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    saveTimeoutRef.current = setTimeout(() => {
      if (!isNonEmptyDoc(body) || !title.trim()) return
      setIsSaving(true)
      // 칸에 쳐 두고 안 넣은 태그는 자동 저장에 안 실린다 — 그래서 '' 로 찍는다.
      const snap = snapshotOf(title, body, tags, '')
      update.mutate(buildPayload(tags), {
        onSuccess: () => {
          setLastSaved(new Date())
          setSavedSnap(snap)
        },
        onSettled: () => setIsSaving(false),
      })
    }, 1500)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, body, title, tags, update, buildPayload])

  // Skip the initial render so loading an article doesn't trigger a no-op save.
  const firstRunRef = useRef(true)
  useEffect(() => {
    if (firstRunRef.current) {
      firstRunRef.current = false
      return
    }
    scheduleAutosave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, tags])

  // ── Tags ──
  // Commit the current tag-draft into the list — used by the 추가 button / Enter
  // AND folded in automatically on save (so a typed-but-not-added tag isn't lost).
  // Returns the resulting tag list.
  const commitTag = (): string[] => {
    const t = tagDraft.trim()
    if (!t || t.length > ARTICLE_TAG_MAX || tags.includes(t) || !canAddTag) {
      return tags
    }
    const next = [...tags, t]
    setTags(next)
    setTagDraft('')
    return next
  }
  const removeTag = (t: string) => setTags((prev) => prev.filter((x) => x !== t))

  // ── Validation shared by both save paths ──
  function validate(tagsArg: string[]): CreateArticleInput | null {
    if (!hasTitle) {
      setError(`제목을 ${ARTICLE_TITLE_MIN}~${ARTICLE_TITLE_MAX}자로 입력해 주세요.`)
      return null
    }
    if (!isNonEmptyDoc(body)) {
      setError('본문을 입력해 주세요.')
      return null
    }
    const parsed = createArticleSchema.safeParse(buildPayload(tagsArg))
    if (!parsed.success) {
      setError('입력값을 확인해 주세요.')
      return null
    }
    return parsed.data
  }

  const handleSaveDraft = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    setError(null)
    // Fold a typed-but-not-added tag into the list before saving.
    const nextTags = commitTag()
    const payload = validate(nextTags)
    if (!payload) return
    setIsSaving(true)
    const snap = snapshotOf(title, body, nextTags, '')
    if (isEdit) {
      update.mutate(payload, {
        onSuccess: () => {
          setLastSaved(new Date())
          setSavedSnap(snap)
        },
        onError: (e) => setError(describeSubmitError(e)),
        onSettled: () => setIsSaving(false),
      })
    } else {
      create.mutate(payload, {
        onSuccess: (article) => {
          setLastSaved(new Date())
          allowLeave()
          void navigate({ to: '/articles/$id/edit', params: { id: article.id } })
        },
        onError: (e) => setError(describeSubmitError(e)),
        onSettled: () => setIsSaving(false),
      })
    }
  }

  const handlePublish = () => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
    setError(null)
    // Fold a typed-but-not-added tag into the list before saving.
    const payload = validate(commitTag())
    if (!payload) return
    setIsSaving(true)
    const go = (id: string) => {
      allowLeave()
      return navigate({ to: '/articles/$id', params: { id } })
    }
    if (isEdit) {
      update.mutate(payload, {
        onSuccess: () => {
          if (status === 'draft') {
            // Draft → submit for review, then open the (author-visible) detail.
            submit.mutate(undefined, {
              onSuccess: () => void go(articleId!),
              onError: (e) => {
                setError(describeSubmitError(e))
                setIsSaving(false)
              },
            })
          } else {
            setIsSaving(false)
            void go(articleId!)
          }
        },
        onError: (e) => {
          setError(describeSubmitError(e))
          setIsSaving(false)
        },
      })
    } else {
      create.mutate(
        { ...payload, submit: true },
        {
          onSuccess: (article) => void go(article.id),
          onError: (e) => setError(describeSubmitError(e)),
          onSettled: () => setIsSaving(false),
        },
      )
    }
  }

  const publishLabel =
    status === 'published' ? '수정 게시' : status === 'pending' ? '수정 완료' : '제출'

  const STATUS_META: Record<ArticleStatus, { label: string; cls: string }> = {
    draft: { label: '초안', cls: 'bg-surface-muted text-text-muted' },
    pending: { label: '검수 대기', cls: 'bg-primary-soft text-primary' },
    published: { label: '발행됨', cls: 'bg-success-soft text-success' },
  }
  const statusLabel = STATUS_META[status].label
  const statusClass = STATUS_META[status].cls

  return (
    // min-h-full + flex 사슬: 카드(flex-1) → 본문 칸(flex-1) 이 남는 세로 공간을 채워 폼이 페이지에 꽉 찬다.
    <div className="flex min-h-full flex-col pb-24">
      {/* Top bar */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-lg font-bold text-text">아티클 작성</h1>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="rounded-lg px-3 py-1.5 text-sm font-medium text-text-muted transition-colors hover:bg-surface-muted disabled:opacity-50"
          >
            초안 저장
          </button>
          <motion.button
            type="button"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handlePublish}
            disabled={isSaving || !hasTitle}
            className={cn(
              'flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-colors disabled:opacity-50',
              !hasTitle
                ? 'border border-border bg-surface text-text-subtle'
                : 'bg-primary text-primary-foreground hover:bg-primary-hover',
            )}
          >
            <Send className="h-4 w-4" />
            {publishLabel}
          </motion.button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div
          role="alert"
          className="mb-4 flex items-center justify-between rounded-lg bg-danger-soft px-4 py-3 text-sm text-danger"
        >
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} aria-label="닫기">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Status row */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-[13px] text-text-muted">
        <span className={cn('rounded-full px-2 py-0.5 font-medium', statusClass)}>
          {statusLabel}
        </span>
        <span className="text-border-strong">·</span>
        <span>{charCount.toLocaleString()}자</span>
        <span className="text-border-strong">·</span>
        <span className="inline-flex items-center gap-1">
          {isSaving ? (
            <>
              <Cloud className="h-3.5 w-3.5 animate-pulse" />
              저장 중…
            </>
          ) : lastSaved ? (
            <>
              <Check className="h-3.5 w-3.5 text-success" />
              저장됨
            </>
          ) : (
            <span className="text-text-subtle">{isEdit ? '자동 저장' : '작성 중'}</span>
          )}
        </span>
      </div>

      {/* Editor card */}
      <div className="flex flex-1 flex-col rounded-2xl bg-surface p-2.5 ring-1 ring-border sm:p-4">
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="제목 평어체로 작성"
          // 본문(.tiptap-content)과 같은 명조체·줄높이·굵기. 크기는 medium(16px).
          className="tiptap-serif mb-3 w-full border-none bg-transparent text-base leading-[1.75] text-text outline-none placeholder:text-text-subtle"
        />

        <div className="flex flex-1 flex-col border-t border-border pt-3">
          <RichEditor value={body} onChange={setBody} ariaLabel="기사 본문" paperClassName="-mx-2.5 sm:-mx-4" />
        </div>

        {/* Tags */}
        <div className="mt-3 border-t border-border pt-3">
          <label htmlFor="article-tag" className="mb-2 block text-sm font-medium text-text">
            태그 (선택)
          </label>
          <div className="flex gap-2">
            <input
              id="article-tag"
              type="text"
              value={tagDraft}
              placeholder="예: 창업, 경험담"
              maxLength={ARTICLE_TAG_MAX}
              disabled={!canAddTag}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  commitTag()
                }
              }}
              className="h-10 min-w-0 flex-1 rounded-lg border border-border bg-surface px-3 text-sm text-text outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:bg-surface-muted"
            />
            <Button
              type="button"
              variant="secondary"
              disabled={!canAddTag || tagDraft.trim() === ''}
              onClick={() => commitTag()}
            >
              추가
            </Button>
          </div>
          {tags.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2">
              {tags.map((t) => (
                <li key={t} className="inline-flex items-center gap-0.5 rounded-md bg-brand-soft py-0.5 pl-2 text-xs font-medium text-brand-strong">
                  #{t}
                  <button
                    type="button"
                    className="-my-2.5 -mr-2.5 ml-0.5 inline-flex min-h-11 min-w-11 items-center justify-center text-brand-strong"
                    aria-label={`태그 ${t} 삭제`}
                    onClick={() => removeTag(t)}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Tips */}
      <div className="mt-4 rounded-xl bg-surface px-4 py-3 text-[13px] text-text-muted ring-1 ring-border">
        <p className="font-medium text-text">작성 팁</p>
        <ul className="mt-1 list-disc space-y-0.5 pl-4">
          <li>사실 위주로 평어체를 사용하자. 어줍잖은 경어체는 이곳에서 만들려는 분위기가 아니다.</li>
          <li>사람·회사 실명은 명예훼손의 위험이 있으므로 밝히지 말자.</li>
          <li>아티클은 관리자 검수 후 등록된다. 비방·광고성 글을 올리는 경우, 사라질 수 있다.</li>
        </ul>
      </div>
    </div>
  )
}
